import { useState, useMemo, useRef, useEffect } from "react"
import { X, Search, AlertTriangle, AlertCircle, Info } from "lucide-react"

interface Parasite {
  code: string
  name: string
  description: string
  severity: "low" | "medium" | "high" | "critical"
}

// Parasite data - commonly found parasites grouped by severity
const PARASITES: Parasite[] = [
  { code: "ICH", name: "Ichthyophthirius multifiliis", description: "White spot disease", severity: "high" },
  { code: "FLU", name: "Gyrodactylus species", description: "Skin/gill fluke", severity: "medium" },
  { code: "DAC", name: "Dactylogyrus species", description: "Gill fluke", severity: "high" },
  { code: "COS", name: "Costia necatrix", description: "Flagellate parasite", severity: "medium" },
  { code: "TRI", name: "Trichodina species", description: "Ciliate parasite", severity: "medium" },
  { code: "CHI", name: "Chilodonella cyprini", description: "Ciliate parasite", severity: "medium" },
  { code: "ARG", name: "Argulus species", description: "Fish louse", severity: "low" },
  { code: "LER", name: "Lernaea species", description: "Anchor worm", severity: "medium" },
  { code: "ANI", name: "Anisakis species", description: "Roundworm", severity: "critical" },
  { code: "DIP", name: "Diplostomum species", description: "Eye fluke", severity: "medium" },
  { code: "STR", name: "Streptococcus species", description: "Bacterial infection", severity: "critical" },
  { code: "AER", name: "Aeromonas hydrophila", description: "Bacterial septicemia", severity: "high" },
  { code: "VIB", name: "Vibrio species", description: "Vibriosis", severity: "high" },
  { code: "COL", name: "Flavobacterium columnare", description: "Columnaris disease", severity: "high" },
  { code: "FUN", name: "Saprolegnia species", description: "Water mold/fungus", severity: "medium" },
  { code: "FIN", name: "Fin Rot", description: "Bacterial fin erosion", severity: "medium" },
  { code: "AGD", name: "Amoebic Gill Disease", description: "Gill disease in salmon", severity: "high" },
  { code: "SEA", name: "Sea Lice", description: "Copepod parasites", severity: "medium" },
  { code: "WSSV", name: "White Spot Syndrome Virus", description: "Prawn viral disease", severity: "critical" },
  { code: "VNN", name: "Viral Nervous Necrosis", description: "Brain infection", severity: "critical" },
]

interface ParasiteSelectProps {
  value: string[] // Array of parasite codes
  onChange: (codes: string[]) => void
  disabled?: boolean
  hasError?: boolean
}

const SEVERITY_ORDER = ["critical", "high", "medium", "low"] as const
const SEVERITY_COLORS = {
  critical: "bg-red-100 text-red-800 border-red-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
  low: "bg-green-100 text-green-800 border-green-200",
}
const SEVERITY_ICONS = {
  critical: AlertTriangle,
  high: AlertCircle,
  medium: Info,
  low: Info,
}

/**
 * Searchable multi-select for parasites.
 * Groups by severity with color-coded chips.
 */
export function ParasiteSelect({
  value = [],
  onChange,
  disabled = false,
  hasError = false,
}: ParasiteSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState("")
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Filter parasites based on search
  const filteredParasites = useMemo(() => {
    const searchLower = search.toLowerCase()
    return PARASITES.filter(
      (p) =>
        !value.includes(p.code) &&
        (p.name.toLowerCase().includes(searchLower) ||
          p.code.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower))
    )
  }, [search, value])

  // Group by severity
  const groupedParasites = useMemo(() => {
    const groups: Record<string, Parasite[]> = {}
    SEVERITY_ORDER.forEach((severity) => {
      const parasites = filteredParasites.filter((p) => p.severity === severity)
      if (parasites.length > 0) {
        groups[severity] = parasites
      }
    })
    return groups
  }, [filteredParasites])

  // Get selected parasites with full data
  const selectedParasites = useMemo(() => {
    return value
      .map((code) => PARASITES.find((p) => p.code === code))
      .filter((p): p is Parasite => !!p)
  }, [value])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const addParasite = (code: string) => {
    if (!value.includes(code)) {
      onChange([...value, code])
    }
    setSearch("")
    inputRef.current?.focus()
  }

  const removeParasite = (code: string) => {
    onChange(value.filter((c) => c !== code))
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Selected chips and search input */}
      <div
        className={`min-h-[42px] p-2 border rounded-lg transition-colors ${
          hasError
            ? "border-red-300 focus-within:ring-red-500/20 focus-within:border-red-500"
            : "border-slate-200/50 focus-within:ring-2 focus-within:ring-emerald-500/20 focus-within:border-emerald-500"
        } ${disabled ? "bg-gray-50 opacity-60" : "bg-white"}`}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        <div className="flex flex-wrap gap-1.5">
          {/* Selected chips */}
          {selectedParasites.map((parasite) => {
            const Icon = SEVERITY_ICONS[parasite.severity]
            return (
              <span
                key={parasite.code}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border ${
                  SEVERITY_COLORS[parasite.severity]
                }`}
              >
                <Icon className="w-3 h-3" />
                <span className="max-w-[120px] truncate">{parasite.name}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeParasite(parasite.code)
                    }}
                    className="ml-0.5 hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            )
          })}

          {/* Search input */}
          <div className="flex-1 min-w-[120px] flex items-center gap-1.5">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setIsOpen(true)}
              placeholder={value.length === 0 ? "Search parasites..." : "Add more..."}
              disabled={disabled}
              className="flex-1 outline-none text-sm bg-transparent placeholder:text-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-auto bg-white border border-slate-200/50 rounded-lg shadow-lg">
          {Object.keys(groupedParasites).length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-500 text-center">
              {search ? "No parasites found" : "All parasites selected"}
            </div>
          ) : (
            Object.entries(groupedParasites).map(([severity, parasites]) => (
              <div key={severity}>
                <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase bg-slate-50 sticky top-0">
                  {severity} Severity ({parasites.length})
                </div>
                {parasites.map((parasite) => (
                  <button
                    key={parasite.code}
                    type="button"
                    onClick={() => addParasite(parasite.code)}
                    className="w-full px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                          SEVERITY_COLORS[parasite.severity]
                        }`}
                      >
                        {parasite.code}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {parasite.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {parasite.description}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
